import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, List, ListItemButton, ListItemText, ListItemAvatar, Avatar, Chip, TextField, Button, Divider, IconButton, Grid, CircularProgress, Badge } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Collapse } from '@mui/material';
import BlockRenderer from '../../components/course/BlockRenderer';
import { designTokens } from '../../theme/designTokens';

interface Question {
    _id: string;
    title: string;
    content: string;
    isResolved: boolean;
    createdAt: string;
    userId: {
        _id: string;
        firstName: string;
        lastName: string;
        role: string;
    };
    lessonId: {
        _id: string;
        title: string;
        type?: string;
        video?: { url: string };
        blocks?: any[];
    };
    courseId: {
        _id: string;
        title: string;
    };
    answers: {
        _id: string;
        content: string;
        createdAt: string;
        userId: {
            firstName: string;
            lastName: string;
            role: string;
        };
    }[];
}

const QuestionsPage = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
    const [loading, setLoading] = useState(true);
    const [answerContent, setAnswerContent] = useState('');
    const [sending, setSending] = useState(false);

    const [filter, setFilter] = useState<'all' | 'pending' | 'resolved'>('pending');
    const [showPreview, setShowPreview] = useState(false);


    useEffect(() => {
        fetchQuestions();
    }, []);

    const fetchQuestions = async () => {
        try {
            const res = await api.get('/api/questions');
            setQuestions(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching questions", err);
            setLoading(false);
        }
    };

    const handleQuestionSelect = async (question: Question) => {
        setSelectedQuestion(question);
        setAnswerContent('');
    };

    const handleSendAnswer = async () => {
        if (!selectedQuestion || !answerContent.trim()) return;

        setSending(true);
        try {
            const res = await api.post(`/api/questions/${selectedQuestion._id}/answer`, {
                content: answerContent
            });

            // Update local state
            const updatedQuestion = res.data;
            setQuestions(questions.map(q => q._id === updatedQuestion._id ? updatedQuestion : q));
            setSelectedQuestion(updatedQuestion);
            setAnswerContent('');
        } catch (err) {
            console.error("Error sending answer", err);
        } finally {
            setSending(false);
        }
    };

    const handleResolve = async () => {
        if (!selectedQuestion) return;

        try {
            const res = await api.patch(`/api/questions/${selectedQuestion._id}/resolve`);
            const updatedQuestion = res.data;
            setQuestions(questions.map(q => q._id === updatedQuestion._id ? updatedQuestion : q));
            setSelectedQuestion(updatedQuestion);
        } catch (err) {
            console.error("Error resolving question", err);
        }
    };

    const filteredQuestions = questions.filter(q => {
        if (filter === 'pending') return !q.isResolved;
        if (filter === 'resolved') return q.isResolved;
        return true;
    });

    if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: 'auto', md: 'calc(100vh - 100px)' }, gap: 2, p: 2 }}>
            {/* Left List */}
            <Paper sx={{ width: { xs: '100%', md: 350 }, height: { xs: '300px', md: 'auto' }, display: 'flex', flexDirection: 'column', ...designTokens.glassCard }}>
                <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                    <Typography variant="h6" gutterBottom>Questions</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                            label="En attente"
                            color={filter === 'pending' ? 'primary' : 'default'}
                            onClick={() => setFilter('pending')}
                            size="small"
                        />
                        <Chip
                            label="Résolues"
                            color={filter === 'resolved' ? 'success' : 'default'}
                            onClick={() => setFilter('resolved')}
                            size="small"
                        />
                        <Chip
                            label="Toutes"
                            onClick={() => setFilter('all')}
                            size="small"
                            variant={filter === 'all' ? 'filled' : 'outlined'}
                        />
                    </Box>
                </Box>
                <List sx={{ overflow: 'auto', flexGrow: 1 }}>
                    {filteredQuestions.map(question => (
                        <ListItemButton
                            key={question._id}
                            selected={selectedQuestion?._id === question._id}
                            onClick={() => handleQuestionSelect(question)}
                            alignItems="flex-start"
                        >
                            <ListItemAvatar>
                                <Avatar>{question.userId?.firstName?.[0]}</Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="subtitle2" noWrap sx={{ maxWidth: 140 }}>
                                            {question.userId?.firstName} {question.userId?.lastName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(question.createdAt).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                }
                                secondary={
                                    <React.Fragment>
                                        <Typography component="span" variant="body2" color="text.primary" noWrap display="block">
                                            {question.title}
                                        </Typography>
                                        <Typography component="span" variant="caption" color="text.secondary" noWrap display="block">
                                            {question.courseId?.title}
                                        </Typography>
                                    </React.Fragment>
                                }
                            />
                        </ListItemButton>
                    ))}
                    {filteredQuestions.length === 0 && (
                        <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                            Aucune question trouvée.
                        </Box>
                    )}
                </List>
            </Paper>

            {/* Right Detail & Player */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
                {selectedQuestion ? (
                    <>
                        {/* Top: Player / Context */}
                        <Paper sx={{ p: 2, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', ...designTokens.glassCard }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Contexte : {selectedQuestion.lessonId?.title}</Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={showPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        onClick={() => setShowPreview(!showPreview)}
                                    >
                                        {showPreview ? "Masquer la leçon" : "Voir la leçon"}
                                    </Button>
                                    <Button
                                        variant={selectedQuestion.isResolved ? "outlined" : "contained"}
                                        color={selectedQuestion.isResolved ? "success" : "primary"}
                                        startIcon={<CheckCircleIcon />}
                                        onClick={handleResolve}
                                    >
                                        {selectedQuestion.isResolved ? "Marquer comme non résolu" : "Marquer comme résolu"}
                                    </Button>
                                </Box>
                            </Box>

                            <Collapse in={showPreview}>
                                <Box sx={{ height: 500, bgcolor: '#f5f5f5', borderRadius: 2, position: 'relative', mb: 2 }}>
                                    {selectedQuestion.lessonId?._id ? (
                                        <iframe
                                            src={`/learner/course/${selectedQuestion.courseId._id}?lessonId=${selectedQuestion.lessonId._id}&mode=preview`}
                                            style={{ width: '100%', height: '100%', border: 'none' }}
                                            title="Course Preview"
                                        />
                                    ) : (
                                        <Box sx={{ p: 2 }}>
                                            <Typography>Impossible de charger le contenu de la leçon.</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Collapse>
                        </Paper>

                        {/* Bottom: Q&A Thread */}
                        <Paper sx={{ p: 2, maxHeight: 400, display: 'flex', flexDirection: 'column', overflow: 'hidden', ...designTokens.glassCard }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold">{selectedQuestion.title}</Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>{selectedQuestion.content}</Typography>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
                                {selectedQuestion.answers.map(answer => (
                                    <Box key={answer._id} sx={{ mb: 2, display: 'flex', gap: 1, flexDirection: answer.userId.role === 'learner' ? 'row' : 'row-reverse' }}>
                                        <Avatar sx={{ width: 32, height: 32 }}>{answer.userId.firstName[0]}</Avatar>
                                        <Paper sx={{ p: 1.5, bgcolor: answer.userId.role === 'learner' ? 'white' : '#e3f2fd', maxWidth: '80%' }}>
                                            <Typography variant="body2">{answer.content}</Typography>
                                            <Typography variant="caption" color="text.secondary" display="block" textAlign="right">
                                                {new Date(answer.createdAt).toLocaleString()}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Votre réponse..."
                                    value={answerContent}
                                    onChange={(e) => setAnswerContent(e.target.value)}
                                    multiline
                                    maxRows={3}
                                />
                                <Button
                                    variant="contained"
                                    endIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                    onClick={handleSendAnswer}
                                    disabled={!answerContent.trim() || sending}
                                >
                                    Envoyer
                                </Button>
                            </Box>
                        </Paper>
                    </>
                ) : (
                    <Paper sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', ...designTokens.glassCard }}>
                        <Typography color="text.secondary">Sélectionnez une question pour voir les détails.</Typography>
                    </Paper>
                )}
            </Box>
        </Box>
    );
};

export default QuestionsPage;
